import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { customerAPI, broadbandAPI } from '../services/api';
import SearchableSelect from '../components/common/SearchableSelect';
import { ArrowLeft, Loader, CheckCircle2, RefreshCw, Wifi, AlertTriangle, Search, Sparkles } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const PAGE_SIZE = 50;

const SCOPES = [
  { key: 'live', label: 'Live Service', hint: 'broadband.isActive: true — urgent, being silently skipped by the monthly cron' },
  { key: 'dormant', label: 'Dormant/Inactive', hint: 'broadband.isActive: false — service not currently live' },
  { key: 'all', label: 'All', hint: 'Both groups together' },
];

export default function NeedsPlanPage() {
  useDarkReady();
  const navigate = useNavigate();
  const [scope, setScope] = useState('live');
  const [rows, setRows] = useState(null); // null = loading
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState({}); // customerId -> planId
  const [busyId, setBusyId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [autoAssigning, setAutoAssigning] = useState(false);

  const load = useCallback(async (scopeToLoad) => {
    setRows(null);
    try {
      const [needsPlanRes, plansRes] = await Promise.all([
        customerAPI.getNeedsPlan(scopeToLoad),
        broadbandAPI.getPlans({ activeOnly: true }),
      ]);
      setRows(needsPlanRes.data.data);
      setPlans((plansRes.data.data || []).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load');
      setRows([]);
    }
  }, []);

  useEffect(() => { setPage(1); load(scope); }, [scope, load]);

  const refresh = async () => { setRefreshing(true); await load(scope); setRefreshing(false); };

  // A last-invoice hint is only worth defaulting the dropdown to if its plan name
  // exactly matches a currently active plan — otherwise (renamed/discontinued plan,
  // or no invoice at all) the admin has to pick manually.
  const matchedPlanId = (row) => {
    if (!row.lastInvoice) return '';
    const match = plans.find(p => p.name === row.lastInvoice.planName);
    return match ? match._id : '';
  };

  useEffect(() => {
    if (!rows || !plans.length) return;
    setSelected(prev => {
      const next = { ...prev };
      for (const r of rows) {
        if (next[r.id] === undefined) next[r.id] = matchedPlanId(r);
      }
      return next;
    });
  }, [rows, plans]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.name.toLowerCase().includes(q) ||
      (r.area || '').toLowerCase().includes(q) ||
      (r.username || '').toLowerCase().includes(q) ||
      (r.mobile || '').includes(q) ||
      (r.lastInvoice?.planName || '').toLowerCase().includes(q) ||
      String(r.billingNumber).includes(q)
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Smart Suggestions — only the unambiguous ones: last invoice's plan name is an
  // EXACT match to a currently active plan. Never a fuzzy/price-based guess.
  const matchedRows = useMemo(() => {
    if (!rows || !plans.length) return [];
    return rows.filter(r => matchedPlanId(r));
  }, [rows, plans]); // eslint-disable-line

  const autoAssign = async () => {
    if (!matchedRows.length) return;
    if (!window.confirm(
      `Auto-assign ${matchedRows.length} customer${matchedRows.length === 1 ? '' : 's'} to their exact-match plan from their last invoice?\n\n` +
      'Only customers whose last invoice plan name exactly matches a current active plan are included — nothing ambiguous is guessed.'
    )) return;
    setAutoAssigning(true);
    try {
      const { data } = await customerAPI.autoAssignPlans(scope);
      toast.success(`${data.data.count} customer${data.data.count === 1 ? '' : 's'} auto-assigned`);
      await load(scope);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Auto-assign failed');
    } finally {
      setAutoAssigning(false);
    }
  };

  const assign = async (row) => {
    const planId = selected[row.id];
    if (!planId) { toast.error('Pick a plan first'); return; }
    setBusyId(row.id);
    try {
      await customerAPI.assignPlan(row.id, planId);
      toast.success(`${row.name} assigned`);
      setRows(prev => prev.filter(r => r.id !== row.id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Assign failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
          <ArrowLeft size={14} />
        </button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Assign Broadband Plans</h2>
          <p className="text-muted text-small" style={{ margin: 0 }}>
            Broadband customers with no plan assigned, shown with their last invoice's plan as a hint.
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" disabled={refreshing} onClick={refresh}>
          {refreshing ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={13} />}
        </button>
      </div>

      <div className="flex items-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
        {SCOPES.map(s => (
          <button
            key={s.key}
            className={`btn btn-sm ${scope === s.key ? 'btn-primary' : 'btn-secondary'}`}
            title={s.hint}
            onClick={() => setScope(s.key)}
          >
            {s.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ position: 'relative' }}>
          <Search size={13} style={{ position: 'absolute', left: 8, top: 9, color: 'var(--text-muted)' }} />
          <input
            className="input"
            style={{ paddingLeft: 26, width: 220 }}
            placeholder="Search name, mobile, area, username, plan…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      {scope !== 'live' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '.82rem', color: 'var(--c-92400e)' }}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            These customers have <strong>broadband.isActive: false</strong> — their service isn't currently flagged live.
            Every "last invoice" hint here comes from the historical Vyapar/HI.xlsx import, not a live payment collected
            through this app, and can be cross-attributed from a linked CATV account sharing the same address — it's a
            hint, not proof the customer is an active broadband subscriber. Verify before assigning if unsure, and note
            that assigning a plan here does <strong>not</strong> flip broadband.isActive — that's a separate decision.
          </div>
        </div>
      )}

      {matchedRows.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--cb-ecfdf5)', border: '1px solid var(--cb-a7f3d0)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
          <Sparkles size={18} style={{ flexShrink: 0, color: 'var(--c-059669)' }} />
          <div style={{ flex: 1, fontSize: '.85rem', color: 'var(--c-065f46)' }}>
            <strong>Smart Suggestion:</strong> {matchedRows.length} customer{matchedRows.length === 1 ? '' : 's'} — {matchedRows.slice(0, 4).map(r => r.name).join(', ')}{matchedRows.length > 4 ? `, +${matchedRows.length - 4} more` : ''} — can be auto-assigned, their last invoice's plan name exactly matches a current active plan.
          </div>
          <button className="btn btn-primary btn-sm" disabled={autoAssigning} onClick={autoAssign}>
            {autoAssigning ? <Loader size={13} style={{ animation: 'spin 1s linear infinite', marginRight: 5 }} /> : null}
            {autoAssigning ? 'Assigning…' : `Auto-Assign All (${matchedRows.length})`}
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0 }}>
            <Wifi size={15} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--primary)' }} />
            {rows === null ? 'Loading…' : `${filtered.length} customer${filtered.length === 1 ? '' : 's'}${search ? ` (of ${rows.length})` : ''}`}
          </h3>
        </div>
        <div className="card-body">
          {rows === null ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <CheckCircle2 size={28} style={{ marginBottom: 8, color: 'var(--c-059669)' }} />
              <div>{rows.length === 0 ? 'All caught up in this view.' : 'No matches for that search.'}</div>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                      <th>Billing #</th>
                      <th>Name</th>
                      <th>Area</th>
                      <th>Username</th>
                      {scope === 'all' && <th>Live?</th>}
                      <th>Last Invoice Hint</th>
                      <th>Assign Plan</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(r => {
                      const matched = matchedPlanId(r);
                      return (
                        <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td>{r.billingNumber}</td>
                          <td>{r.name}<div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{r.mobile}</div></td>
                          <td>{r.area || '—'}</td>
                          <td>{r.username || '—'}</td>
                          {scope === 'all' && (
                            <td><span className={`badge badge-${r.isActive ? 'active' : 'pending'}`} style={{ fontSize: '.68rem' }}>{r.isActive ? 'Live' : 'Dormant'}</span></td>
                          )}
                          <td>
                            {r.lastInvoice ? (
                              <span style={{ color: matched ? 'var(--c-065f46)' : 'var(--c-92400e)' }}>
                                {r.lastInvoice.planName} (₹{fmt(r.lastInvoice.price)}){!matched && ' — no current plan match'}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>No invoice history</span>
                            )}
                          </td>
                          <td>
                            <SearchableSelect
                              style={{ minWidth: 240 }}
                              value={selected[r.id] || ''}
                              onChange={v => setSelected(prev => ({ ...prev, [r.id]: v }))}
                              placeholder="Search plan…"
                              options={plans.map(p => ({ value: p._id, label: `${p.name} (₹${fmt(p.price)})` }))}
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-primary btn-sm"
                              disabled={busyId === r.id || !selected[r.id]}
                              onClick={() => assign(r)}
                            >
                              {busyId === r.id ? 'Assigning…' : 'Assign'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-3">
                  <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                  <span className="text-small text-muted">Page {page} of {totalPages}</span>
                  <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
